import { z } from "zod";

/* ===========================================================================
 * US-E13.F0-0082-S26 Home Summary "Nhịp minh bạch" (@UC0074) — aggregate READ-ONLY (Phase 8.5).
 *
 *  - GET /v1/me/home-summary — dữ liệu hero Home trong 1 call, COMPOSE service có sẵn
 *    (fund-dashboard txTotals/weekRange + balance O(1) + payment-board counts + groups.listMyGroups +
 *    ActionItemsService.totalCount + ActivityFeedService peek). Redaction fail-closed per-fund:
 *    Member + fund transparency=private → OMIT balance/tiền (balanceMinor=null; tiền tuần không cộng).
 *
 * Tiền STRING minor unit (B1, no float). balanceMinor=null ⇔ fund bị redact (fail-closed). READ-ONLY.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;
const moneyString = z.string().regex(MONEY_INTEGER_STRING_PATTERN);

/** 1 quỹ trong "Quỹ của tôi" (rút gọn). balanceMinor=null khi fund bị redact (Member + private). */
export const homeSummaryFundSchema = z
  .object({
    fundId: z.string().uuid(),
    groupName: z.string().nullable(),
    fundName: z.string(),
    /** Số dư O(1) (STRING minor unit, B1) — null khi redacted (fail-closed). */
    balanceMinor: moneyString.nullable(),
    memberCount: z.number().int().nonnegative(),
  })
  .strict();

export type HomeSummaryFund = z.infer<typeof homeSummaryFundSchema>;

/** 1 mục peek "Gần đây" (projection từ activity-feed). amountMinor null khi redacted / không có tiền. */
export const homeSummaryActivitySchema = z
  .object({
    id: z.string().uuid(),
    kind: z.string(),
    occurredAt: z.string().datetime(),
    /** Số tiền sự kiện (STRING minor unit, B1) — null khi redacted / sự kiện không có tiền. */
    amountMinor: moneyString.nullable(),
  })
  .strict();

export type HomeSummaryActivity = z.infer<typeof homeSummaryActivitySchema>;

/**
 * Response hero Home "Nhịp minh bạch · tuần này" (@UC0074).
 *  - weeklyTransparencyPct 0..100 (tỉ lệ quỹ public / tổng quỹ user).
 *  - weekInMinor / weekOutMinor: tiền vào/ra TUẦN NÀY (chỉ cộng quỹ user KHÔNG bị redact).
 *  - fullyPaidCount / totalDueCount: đóng-đủ tuần (từ member_dues của quỹ user).
 *  - actionablesCount: ActionItemsService.totalCount.
 *  - funds: my funds summary (balanceMinor=null khi redacted).
 *  - recentActivity: peek ≤5 hoạt động (redacted).
 */
export const homeSummaryResponseSchema = z
  .object({
    weeklyTransparencyPct: z.number().min(0).max(100),
    weekInMinor: moneyString,
    weekOutMinor: moneyString,
    fullyPaidCount: z.number().int().nonnegative(),
    totalDueCount: z.number().int().nonnegative(),
    actionablesCount: z.number().int().nonnegative(),
    funds: z.array(homeSummaryFundSchema),
    recentActivity: z.array(homeSummaryActivitySchema),
  })
  .strict();

export type HomeSummaryResponse = z.infer<typeof homeSummaryResponseSchema>;
